<template>
  <div class="mozio">
    <moz-title>
      <template slot="title">
        {{ searchTitle | i18n(locale) }}
      </template>
      <template
        v-if="isNoResultMode"
        slot="subtitle"
      >
        <span
          v-for="(text, index) in subTitleTexts"
          :key="index"
        >
          {{ text }}
        </span>
      </template>
    </moz-title>
    <template v-if="errorKey">
      <moz-error>
        <span
          v-for="(text, index) in errorTexts"
          :key="index"
        >
          {{ text }}
        </span>
      </moz-error>
    </template>
    <template v-else>
      <div
        v-if="isLoading"
        class="loader-wrap"
      >
        <moz-loader>
          {{ 'START_SEARCH_SUBTITLE_NEW' | i18n(locale) }}
        </moz-loader>
      </div>

      <moz-transfer-card-group
        :session-id="searchId"
        :currency="currency"
        :groups="groupedResult"
        :locale="locale"
        :vehicle-texts="vehicles"
      />
    </template>
  </div>
</template>

<script>
import {
  startPollSearch,
  startSearch,
  filterResult,
  isNotKiwiTaxiProvider,
  setCustomRequestHeadersProvider,
  groupByCategories,
  sortGroupByPrice,
  fetchVehicles,
} from './services/mozio';
import { i18n as i18nFilter } from './vueFilters';
import { MODES, LANGUAGE_MAP, SEARCH_RESULT_ERROR_TYPES } from './constants';

import MozLoader from './components/MozLoader';
import MozTitle from './components/MozTitle';
import MozError from './components/MozError';
import MozTransferCardGroup from './components/MozTransferCard/Group.vue';
import { GTM_EVENTS, sendGTM } from '../utils/gtm';

const GROUP_CACHE = [];

export default {
  components: {
    MozLoader,
    MozTransferCardGroup,
    MozTitle,
    MozError,
  },
  filters: {
    i18n: i18nFilter,
  },
  props: {
    from: {
      type: String,
      default: '',
    },
    to: {
      type: String,
      default: '',
    },
    currency: {
      type: String,
      default: '',
    },
    passengers: {
      type: Number,
      default: 1,
    },
    mode: {
      type: String,
      default: MODES.NO_RESULTS,
    },
    locale: {
      type: String,
      default: 'ru',
    },
    clarifiedSearch: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      isLoading: false,
      searchId: null,
      groupedResult: [],
      errorKey: '',
      vehicles: {},
    };
  },
  computed: {
    lang() {
      return LANGUAGE_MAP[this.locale].api;
    },
    subTitleTexts() {
      return this.$options.filters.i18n('START_SEARCH_SUBTITLE', this.locale);
    },
    errorTexts() {
      switch (this.mode) {
        case MODES.NO_CAPACITY:
          return this.$options.filters.i18n('MOZIO_NO_CAPACITY', this.locale);
        case MODES.NO_SALES:
          return [this.$options.filters.i18n('MOZIO_NO_RESULT', this.locale)];
        default:
          return this.$options.filters.i18n(this.errorKey, this.locale);
      }
    },
    searchTitle() {
      return this.isNoResultMode ? 'START_SEARCH' : 'MOZIO_TRANSFER_TITLE';
    },
    isNoResultMode() {
      return this.mode === MODES.NO_RESULTS;
    },
  },
  mounted() {
    setCustomRequestHeadersProvider(this.setHeaders);
    this.loadVehicles();
    this.searchTransfets();
  },
  methods: {
    async searchTransfets() {
      this.isLoading = true;
      const params = this.getSearchParams();
      try {
        const { search_id: searchId } = await this.startSearchWithHandler(params);
        this.searchId = searchId;
        await this.startPollSearch();
        this.$nextTick(this.onPollEnd);
      } catch (err) {
        this.errorKey = err.message;
      }
    },
    startPollSearch() {
      return startPollSearch(this.searchId, this.onPollSearchHandler);
    },
    async startSearchWithHandler(params) {
      try {
        return await startSearch(params);
      } catch (err) {
        let key = SEARCH_RESULT_ERROR_TYPES.ROUTE_NO_RESULT;
        if (this.clarifiedSearch) {
          key = SEARCH_RESULT_ERROR_TYPES.ROUTE_AND_MOZIO_NO_TRANSFERS;
        }
        this.$emit('empty-result', key);
        throw new Error(key);
      }
    },
    onPollSearchHandler(results) {
      const arrayOfFileters = this.isNoResultMode ? [isNotKiwiTaxiProvider] : [];
      const filteredArray = filterResult(results)(arrayOfFileters);
      this.groupedResult = sortGroupByPrice(groupByCategories([...GROUP_CACHE, ...filteredArray]));
      GROUP_CACHE.push(...filteredArray);
    },
    getSearchParams() {
      const currentDate = new Date();
      const params = {
        /** Exact formatted address where the customer
         * should be picked up (e.g. 37 NE Velmont St, Portland, Oregon)
         */
        start_address: this.from,
        end_address: this.to,
        /** Filter search results according to vehicle capacity */
        num_passengers: this.passengers || 2,
        /** Currency to display the results */
        currency: this.currency,
        /** Datetime when the customer has to be picked up (in iso-8601 format). */
        pickup_datetime: new Date(currentDate.setDate(currentDate.getDate() + 1)).toISOString(),
        /* сценарий отображения мозио (no-results, no-sales, no-capacity) */
        campaign: this.mode,
        /* partner affiliate program */
        ...(
          this.$root.sourceConfig.pap ? { branch: this.$root.sourceConfig.pap } : {}
        ),
      };
      return params;
    },
    async loadVehicles() {
      this.vehicles = await fetchVehicles();
    },
    setHeaders() {
      return {
        LANG: this.lang,
      };
    },
    onPollEnd() {
      if (!this.groupedResult.length) {
        this.errorKey = SEARCH_RESULT_ERROR_TYPES.ROUTE_AND_MOZIO_NO_TRANSFERS;
        this.$emit('empty-result', SEARCH_RESULT_ERROR_TYPES.ROUTE_AND_MOZIO_NO_TRANSFERS);
      } else {
        sendGTM(GTM_EVENTS.MOZIO_ROUTES_SHOW);
      }
      this.isLoading = false;
      GROUP_CACHE.length = 0;
    },
  },
};
</script>


<style lang="scss" scoped>
  .loader-wrap {
    margin: 80px 0 120px;
    text-align: center;
  }
</style>
